import queryString from "query-string";
import { useRouter } from "./useRouter";
import {
  ProductTaxonomyEnum,
  ProductTaxonomyFilterInput,
  TaxonomyOperatorEnum,
} from "../../types/generalTypes";
import { useLanguageContext } from "../context/LanguageContext/LanguageContext";

const EXCLUDED_QUERY_PARAMS = new Set([
  "brand",
  "oferta",
  "tereja",
  "meteshikuarat",
  "tenxehta",
  "order",
  "page",
  "redirect",
  "minPrice",
  "maxPrice",
]);

export const normalizeTaxonomyTerms = (value: unknown): string[] => {
  if (value === null || value === undefined || value === false) {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .flatMap((term) => String(term).split(","))
      .map((term) => term.trim())
      .filter(Boolean);
  }

  return String(value)
    .split(",")
    .map((term) => term.trim())
    .filter(Boolean);
};

export const denormalizeTaxonomyKey = (key: string): string =>
  key.toUpperCase().replace(/_/g, "");

const TAXONOMY_ALIASES: Record<string, ProductTaxonomyEnum> = {
  PRODUCTCATEGORY: ProductTaxonomyEnum.PRODUCTCATEGORY,
  PRODUCT_CAT: ProductTaxonomyEnum.PRODUCTCATEGORY,
  PRODUCTBRAND: ProductTaxonomyEnum.PRODUCTBRAND,
  PRODUCT_BRAND: ProductTaxonomyEnum.PRODUCTBRAND,
  PRODUCT_BRANDS: ProductTaxonomyEnum.PRODUCTBRAND,
  PRODUCTTAG: ProductTaxonomyEnum.PRODUCTTAG,
  PRODUCT_TAG: ProductTaxonomyEnum.PRODUCTTAG,
  PRODUCTTYPE: ProductTaxonomyEnum.PRODUCTTYPE,
  PRODUCT_TYPE: ProductTaxonomyEnum.PRODUCTTYPE,
  SHIPPINGCLASS: ProductTaxonomyEnum.SHIPPINGCLASS,
  PRODUCT_SHIPPING_CLASS: ProductTaxonomyEnum.SHIPPINGCLASS,
  VISIBLEPRODUCT: ProductTaxonomyEnum.VISIBLEPRODUCT,
  PRODUCT_VISIBILITY: ProductTaxonomyEnum.VISIBLEPRODUCT,
};

export const toWooGraphQLTaxonomy = (value: string): string => {
  const key = value.toUpperCase();
  if (TAXONOMY_ALIASES[key]) {
    return TAXONOMY_ALIASES[key];
  }
  if (/^PA[A-Z0-9]/i.test(key) && !key.startsWith("PA_")) {
    return `PA_${key.slice(2)}`;
  }
  return key;
};

export const resolveTaxonomyFromQueryKey = (
  key: string
): ProductTaxonomyEnum | null => {
  if (EXCLUDED_QUERY_PARAMS.has(key.toLowerCase())) {
    return null;
  }

  const normalizedKey = toWooGraphQLTaxonomy(key);
  const denormalizedKey = denormalizeTaxonomyKey(normalizedKey);

  if (
    Object.values(ProductTaxonomyEnum).includes(
      normalizedKey as ProductTaxonomyEnum
    )
  ) {
    return normalizedKey as ProductTaxonomyEnum;
  }

  if (normalizedKey in ProductTaxonomyEnum) {
    return ProductTaxonomyEnum[
      normalizedKey as keyof typeof ProductTaxonomyEnum
    ];
  }

  const enumValueMatch = (
    Object.values(ProductTaxonomyEnum) as string[]
  ).find((value) => denormalizeTaxonomyKey(value) === denormalizedKey);
  if (enumValueMatch) {
    return enumValueMatch as ProductTaxonomyEnum;
  }

  const enumKeyMatch = Object.keys(ProductTaxonomyEnum).find(
    (enumKey) => denormalizeTaxonomyKey(enumKey) === denormalizedKey
  );
  if (enumKeyMatch) {
    return ProductTaxonomyEnum[
      enumKeyMatch as keyof typeof ProductTaxonomyEnum
    ];
  }

  if (/^PA[A-Z0-9_]+$/.test(normalizedKey)) {
    return normalizedKey as ProductTaxonomyEnum;
  }

  return null;
};

export const prepareTaxonomyFiltersFromSearchParams = (
  data: Record<string, unknown> | null | undefined,
  operator?: string
): ProductTaxonomyFilterInput[] => {
  if (!data) {
    return [];
  }

  return Object.keys(data)
    .map((key) => {
      const taxonomy = resolveTaxonomyFromQueryKey(key);
      if (!taxonomy) {
        return null;
      }

      const terms = normalizeTaxonomyTerms(data[key]);
      if (!terms.length) {
        return null;
      }

      return {
        taxonomy,
        terms,
        operator: operator ?? "IN",
      };
    })
    .filter(Boolean) as ProductTaxonomyFilterInput[];
};

export const useShopHandlers = () => {
  const router = useRouter();
  const langCtx = useLanguageContext();
  const prefix = langCtx.getLangPrefix();

  const updateSearchParameters = (
    val: string,
    attribute: string,
    callback: (data: any) => void
  ) => {
    const query: any = queryString.parse(router.location.search);

    const rawValue = query[attribute];
    const value = Array.isArray(rawValue)
      ? rawValue.join(",")
      : (rawValue as string | undefined);

    let newQuery = {
      ...query,
    };

    if (value) {
      if (!val) {
        delete newQuery[attribute];
      } else if (value.split(",").includes(val)) {
        newQuery = {
          ...newQuery,
          [attribute]: value
            .split(",")
            .filter((f: string) => f !== val)
            .join(","),
        };
      } else {
        newQuery = {
          ...newQuery,
          [attribute]: value.concat(`,${val}`),
        };
      }
    } else if (val) {
      newQuery = {
        ...newQuery,
        [attribute]: val,
      };
    }

    const newSearchParams = Object.keys(newQuery)
      .filter(
        (f: any) => newQuery[f] !== "" && newQuery[f] !== null && newQuery[f] !== undefined
      )
      .map((f: any) => `${f}=${newQuery[f]}`);

    router.history.replace({
      pathname: router.location.pathname,
      search: newSearchParams.join("&"),
    });

    callback(newQuery);
  };

  const updateSingleQueryParameter = (
    name: string,
    value: string,
    callback: (field: string, order: string) => void
  ) => {
    const query: any = queryString.parse(router.location.search);
    const parts = value ? value.split("-") : [];
    const orderDirection = parts.length > 1 ? parts.pop() || "" : "";
    const fieldName = parts.join("-");

    let newQuery = {
      ...query,
    };

    //TODO: refactor this
    if (value) {
      newQuery = {
        ...newQuery,
        [name]: value,
      };
    } else {
      delete newQuery[name];
    }

    const newSearchParams = Object.keys(newQuery)
      .filter((f: any) => newQuery[f] !== "" && newQuery[f] != null)
      .map((f: any) => `${f}=${newQuery[f]}`);

    router.history.replace({
      pathname: router.location.pathname,
      search: newSearchParams.join("&"),
    });

    callback(fieldName, orderDirection);
  };

  const updatePathname = (
    newPathName: string,
    resetSearchParameters?: boolean,
    searchParams?: string
  ) => {
    router.history.push({
      pathname: prefix + newPathName,
      search: resetSearchParameters
        ? `${searchParams || ""}`
        : router.location.search,
    });
  };

  const prepareTaxonomyFilters = (
    data: Record<string, unknown> | null | undefined,
    operator?: string
  ) => prepareTaxonomyFiltersFromSearchParams(data, operator);

  const getBrandTaxonomyFilter = (
    brand?: unknown
  ): ProductTaxonomyFilterInput[] | undefined => {
    const terms = normalizeTaxonomyTerms(brand);
    if (!terms.length) {
      return undefined;
    }

    return [
      {
        taxonomy: ProductTaxonomyEnum.PRODUCTBRAND,
        terms,
        operator: TaxonomyOperatorEnum.IN,
      },
    ];
  };

  return {
    updateSearchParameters,
    updateSingleQueryParameter,
    prepareTaxonomyFilters,
    getBrandTaxonomyFilter,
    updatePathname,
  };
};
